import { Formik } from 'formik';
import { useMemo } from 'react';
import { useAlert, Alert, CheckboxWithLabel, ExternalLink, formatCents, SupportPaymentLabel } from '@uplift/ui-v2';
import { bool, object, ObjectSchema } from 'yup';

import { Box, FormGroup, Typography } from '@mui/material';
import { makeStyles } from 'tss-react/mui';
import { useMutation } from '@tanstack/react-query';
import {
  AppointmentType,
  CareType,
  completeOnboarding, isApiError, PublicAppointment, UpdateMatchPreferencesInput
} from '@uplift/api';
import { useCurrentuser } from '@uplift/contexts';
import { useL10n } from '@uplift/hooks';
import { useInsuranceCompaniesQuery, useProviderPublicMatchingProfile, useStates } from '@uplift/queries';
import { l10nEnService } from '@uplift/services';
import { ProviderMedicalRole, SubSystem } from '@uplift/types';

import {
  StepContainer, StepContent, StepForm, StepHeading, StepNavigator, StepNextButton, YourProvider
} from '../../components';
import { SELF_PAY_ID } from '../../utils';
import { ConfirmBookingNextData } from '../types';
import { lateFeeAmountByAppointmentType } from '@uplift/utils';

export type ConfirmBookingProps = {
  onBack: () => void;
  onNext: (data: ConfirmBookingNextData) => void;
  onBookingConflict: () => void;
  matchId: string;
  providerId: string;
  appointment: PublicAppointment;
  stateId: string;
  insuranceId: string;
  careType: CareType;
  termsAccepted: boolean;
  matchPreferencesData: UpdateMatchPreferencesInput;
  isBookingSponsored?: boolean;
};

type ConsentFormValues = {
  treatmentConsent: boolean;
  financialResponsibility: boolean;
  confirmState: boolean;
};

export const ConfirmBooking: React.VFC<ConfirmBookingProps> = props => {
  const l10n = useL10n();
  const { classes } = useConfirmBookingStyles();
  const alert = useAlert();
  const me = useCurrentuser();
  const isStudent = me?.subsystem === SubSystem.higherEd;
  const { data: companies = [] } = useInsuranceCompaniesQuery();
  const { data: publicProviderProfile } = useProviderPublicMatchingProfile(props.providerId, { enabled: !!props.providerId });
  const medicalRole = publicProviderProfile?.user?.medicalRole ?? ProviderMedicalRole.therapist;
  const completeOnboardingMutation = useMutation({
    mutationKey: [ 'completeOnboarding' ],
    mutationFn: completeOnboarding,
    onError: error => {
      if (isApiError(error)) {
        const appointmentErrorMessage = l10n.schedule.createAppointment.errors[error.response.data?.error]?.body;

        if (appointmentErrorMessage) {
          alert.error(appointmentErrorMessage);
          props.onBookingConflict();
          return;
        } else if (error.response.data?.error_key === 'match_archived') {
          alert.error('Session expired. Please refresh the page to be able to finish onboarding.');
          return;
        }

        alert.error(error.message ?? 'Unknown error');
      } else {
        console.error('Error completing onboarding:', error);
        alert.error('An error occurred while completing your booking. Please try again.');
      }
    },
    onSuccess: data => {
      props.onNext({
        intakeFormSubmissionId: data.submissionId
      });
    }
  });

  const onSubmit = async () => {
    await completeOnboardingMutation.mutateAsync({
      timeStart: props.appointment?.fromDatetime,
      providerId: props.providerId,
      matchId: props.matchId,
    });
  };

  const nextButtonText = useMemo(() => {
    if (props.careType === CareType.therapyAndPsychiatry) {
      if (isStudent) {
        return medicalRole === ProviderMedicalRole.therapist
          ? 'Confirm therapy booking'
          : 'Confirm consultation';
      } else {
        return 'Confirm therapy booking';
      }
    } else {
      return 'Confirm';
    }
  }, [ props.careType, isStudent, medicalRole ]);

  const appointmentType = {
    [ProviderMedicalRole.therapist]: AppointmentType.therapy,
    [ProviderMedicalRole.psychiatrist]: AppointmentType.psychiatry,
  }[medicalRole];

  return (
    <Formik
      initialValues={{
        confirmState: props.termsAccepted,
        treatmentConsent: props.termsAccepted,
        privacyPolicy: props.termsAccepted,
        financialResponsibility: props.termsAccepted
      }}
      onSubmit={onSubmit}
      validationSchema={validationSchema(l10n)}
      validateOnMount
    >
      {formik => (
        <StepContainer>
          <StepContent>
            <StepHeading>Review and Book</StepHeading>
            <YourProvider
              className={classes.yourProvider}
              providerId={props.providerId}
              appointment={props.appointment}
              variant="large"
            />
            <StepForm onSubmit={formik.handleSubmit}>
              <ConsentForm stateId={props.stateId} />
            </StepForm>
            <SupportPaymentLabel
              className={classes.paymentLabel}
              isAcceptInsurance={props.insuranceId && props.insuranceId !== SELF_PAY_ID}
              isPsychiatrist={props.careType === CareType.psychiatry}
              text={companies.find(company => company.id === props.insuranceId)?.shortName}
              isBookingSponsored={props.isBookingSponsored}
            />
            {!props?.isBookingSponsored && (
              <>
                <Typography>
                  {l10n.matching.book.reachOut} <Typography component="span" fontWeight={450}>{l10n.matching.book.byContinuing}</Typography>
                </Typography>
                <Box mt={2}>
                  <Alert flat severity="info">
                    Please note that if you cancel within 24 hours of your scheduled appointment time you will be charged a cancellation fee of {formatCents(lateFeeAmountByAppointmentType[appointmentType], 0)}.
                  </Alert>
                </Box>
              </>
            )}

          </StepContent>
          <StepNavigator onBack={props.onBack}>
            <StepNextButton disabled={!formik.isValid || !props.matchId}>
              {nextButtonText}
            </StepNextButton>
          </StepNavigator>
        </StepContainer>
      )}
    </Formik>
  );
};

const useConfirmBookingStyles = makeStyles({ name: 'ConfirmBooking' })(theme => ({
  yourProvider: {
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: theme.shape.borderRadius,
    marginTop: theme.spacing(3),
  },
  paymentLabel: {
    marginTop: theme.spacing(3),
    marginBottom: theme.spacing(0.5),
  }
}));

type ConsentFormProps = {
  stateId: string;
};

const ConsentForm: React.VFC<ConsentFormProps> = props => {
  const l10n = useL10n();
  const { classes } = useConsentFormStyles();
  const { states = [] } = useStates();
  const state = states.find(s => s.id === props.stateId);

  return (
    <div>
      <Typography variant="body1" component="p" className={classes.label} color="textPrimary">
        {l10n.matching.payment.accept}
      </Typography>
      <FormGroup className={classes.formGroup}>
        <CheckboxWithLabel
          name="confirmState"
          color="secondary"
          label={l10n.matching.payment.confirmState(state?.fullName)}
        />
        <CheckboxWithLabel
          name="treatmentConsent"
          color="secondary"
          label={<ExternalLink fontWeight={400} to="https://www.joinuplift.co/tos/treatment" text={l10n.matching.payment.treatmentFormConsent} />}
        />
        <CheckboxWithLabel
          name="financialResponsibility"
          color="secondary"
          label={<ExternalLink fontWeight={400} to="https://www.joinuplift.co/tos/financial" text={l10n.matching.payment.finResponsibility} />}
        />
      </FormGroup>
    </div>
  );
};

const useConsentFormStyles = makeStyles({ name: 'ConsentForm' })(theme => ({
  label: {
    marginBottom: theme.spacing(0.5),
    fontWeight: 450,
  },
  formGroup: {
    paddingLeft: theme.spacing(1.5),
  }
}));


const validationSchema = (l10n: typeof l10nEnService): ObjectSchema<ConsentFormValues> => object({
  confirmState: bool()
    .oneOf([ true ], "Check the box to signify that you've read and agree to the terms")
    .required(l10n.validations.termsOfService),
  treatmentConsent: bool()
    .oneOf([ true ], "Check the box to signify that you've read and agree to the terms")
    .required(l10n.validations.termsOfService),
  financialResponsibility: bool()
    .oneOf([ true ], "Check the box to signify that you've read and agree to the terms")
    .required(l10n.validations.termsOfService),
});
